import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { User, Mail, Shield, Calendar, Edit, Save, X } from 'lucide-react';
import { AppDispatch, RootState } from '@/store';
import { updateUser } from '@/store/slices/usersSlice';
import { getCurrentUser } from '@/store/slices/authSlice';
import { UserRole } from '@/types';
import { Permission } from '@/utils/permissions';
import {
  GlassCard,
  GlassButton,
  GlassInput,
  GlassBadge,
  GlassLoadingSpinner,
  GlassPageHeader,
  SectionReveal,
} from '@/design-system';

export const UserProfilePage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  const { isLoading } = useSelector((state: RootState) => state.users);

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    additionalPermissions: (user?.additionalPermissions as Permission[] | undefined) || [],
  });
  const [privilegeSet, setPrivilegeSet] = useState<Set<Permission>>(new Set((user?.additionalPermissions as Permission[] | undefined) || []));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        additionalPermissions: (user.additionalPermissions as Permission[] | undefined) || [],
      });
      setPrivilegeSet(new Set((user.additionalPermissions as Permission[] | undefined) || []));
    }
  }, [user]);

  const formatRoleLabel = (role: UserRole) => {
    return role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getRoleBadgeVariant = (role: UserRole): 'success' | 'info' | 'warning' | 'default' => {
    switch (role) {
      case UserRole.EXECUTIVE:
        return 'success';
      case UserRole.PMO:
        return 'info';
      case UserRole.IS_OWNER:
        return 'success';
      case UserRole.PROGRAM_LEAD:
        return 'warning';
      case UserRole.FINANCE:
        return 'warning';
      case UserRole.VIEWER:
      default:
        return 'default';
    }
  };

  const getRoleDescription = (role: UserRole): string => {
    switch (role) {
      case UserRole.EXECUTIVE:
        return 'Portfolio-level view and approvals. Can view all dashboards and approve EPICs.';
      case UserRole.PMO:
        return 'Full planning workflow access. Can manage all planning entities and user accounts.';
      case UserRole.IS_OWNER:
        return 'Manage own Investible Structures, EPICs, and Features. Can create and prioritize work items.';
      case UserRole.PROGRAM_LEAD:
        return 'Manage allocations and delivery status for assigned programs. Can update team assignments.';
      case UserRole.FINANCE:
        return 'Cost profiles and financial data access. Can manage cost profiles and view financial reports.';
      case UserRole.VIEWER:
        return 'Read-only access to assigned entities. Cannot modify any data.';
      default:
        return 'No description available.';
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm() || !user) {
      return;
    }

    setIsSubmitting(true);

    try {
      await dispatch(updateUser({
        id: user.id,
        data: {
          ...formData,
          additionalPermissions: Array.from(privilegeSet),
        },
      })).unwrap();

      // Refresh current user data
      await dispatch(getCurrentUser());

      setIsEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (user) {
      setFormData({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        additionalPermissions: (user.additionalPermissions as Permission[] | undefined) || [],
      });
      setPrivilegeSet(new Set((user.additionalPermissions as Permission[] | undefined) || []));
    }
    setErrors({});
    setIsEditing(false);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const privilegeOptions: Array<{ key: string; label: string; permissions: Permission[] }> = [
    {
      key: 'teams',
      label: 'Add/Update Teams',
      permissions: [Permission.CREATE_TEAMS, Permission.UPDATE_TEAMS],
    },
    {
      key: 'programs',
      label: 'Add/Update Programs',
      permissions: [Permission.CREATE_PROGRAMS, Permission.UPDATE_PROGRAMS],
    },
    {
      key: 'investibleStructures',
      label: 'Add/Update Investible Structures',
      permissions: [Permission.CREATE_INVESTIBLE_STRUCTURES, Permission.UPDATE_INVESTIBLE_STRUCTURES],
    },
    {
      key: 'epics',
      label: 'Add/Update EPICs',
      permissions: [Permission.CREATE_EPICS, Permission.UPDATE_EPICS],
    },
    {
      key: 'features',
      label: 'Add/Update Features',
      permissions: [Permission.CREATE_FEATURES, Permission.UPDATE_FEATURES],
    },
  ];

  const togglePrivilege = (perms: Permission[]) => {
    setPrivilegeSet(prev => {
      const next = new Set(prev);
      const hasAll = perms.every(p => next.has(p));
      if (hasAll) {
        perms.forEach(p => next.delete(p));
      } else {
        perms.forEach(p => next.add(p));
      }
      return next;
    });
  };

  if (!user) {
    return (
      <div className="min-h-screen glass-page-bg flex items-center justify-center">
        <GlassLoadingSpinner size="lg" variant="gradient" label="Loading profile..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen glass-page-bg p-6 lg:p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <SectionReveal>
          <GlassPageHeader
            title="My Profile"
            subtitle="Manage your account information and preferences"
            icon={<User className="w-6 h-6" />}
            iconGradient="blue-violet"
            className="mb-6"
            actions={
              !isEditing && (
                <GlassButton
                  variant="primary"
                  onClick={() => setIsEditing(true)}
                  leftIcon={<Edit className="h-4 w-4" />}
                >
                  Edit Profile
                </GlassButton>
              )
            }
          />
        </SectionReveal>

        {/* Profile Card */}
        <SectionReveal delay={0.1}>
          <GlassCard padding="none" className="overflow-hidden">
            {/* Profile Header */}
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-8">
              <div className="flex items-center space-x-4">
                <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                  <User className="w-10 h-10 text-white" />
                </div>
                <div className="text-white">
                  <h2 className="text-2xl font-bold">
                    {user.firstName} {user.lastName}
                  </h2>
                  <div className="flex items-center space-x-2 mt-1 opacity-90">
                    <Mail className="w-4 h-4" />
                    <span>{user.email}</span>
                  </div>
                  <div className="mt-2">
                    <GlassBadge variant={getRoleBadgeVariant(user.role)}>
                      {formatRoleLabel(user.role)}
                    </GlassBadge>
                  </div>
                </div>
              </div>
            </div>

            {/* Profile Content */}
            <div className="p-6 space-y-6">
              {/* Basic Information */}
              <div>
                <h3 className="text-lg font-medium text-foreground mb-4">Basic Information</h3>

                {isEditing ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <GlassInput
                        label="First Name"
                        value={formData.firstName}
                        onChange={(value) => handleInputChange('firstName', value)}
                        error={errors.firstName}
                        disabled={isSubmitting}
                        required
                      />
                      <GlassInput
                        label="Last Name"
                        value={formData.lastName}
                        onChange={(value) => handleInputChange('lastName', value)}
                        error={errors.lastName}
                        disabled={isSubmitting}
                        required
                      />
                    </div>
                    <GlassInput
                      label="Email Address"
                      type="email"
                      value={formData.email}
                      onChange={(value) => handleInputChange('email', value)}
                      error={errors.email}
                      disabled={isSubmitting}
                      required
                    />

                    {/* Action Buttons */}
                    <div className="flex items-center space-x-3 pt-4">
                      <GlassButton
                        variant="primary"
                        onClick={handleSave}
                        disabled={isSubmitting}
                        leftIcon={isSubmitting ? undefined : <Save className="h-4 w-4" />}
                      >
                        {isSubmitting ? (
                          <div className="flex items-center gap-2">
                            <GlassLoadingSpinner size="sm" />
                            Saving...
                          </div>
                        ) : (
                          'Save Changes'
                        )}
                      </GlassButton>
                      <GlassButton
                        variant="secondary"
                        onClick={handleCancel}
                        disabled={isSubmitting}
                        leftIcon={<X className="h-4 w-4" />}
                      >
                        Cancel
                      </GlassButton>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-muted-foreground/60">First Name</label>
                      <p className="mt-1 text-sm text-foreground">{user.firstName}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-muted-foreground/60">Last Name</label>
                      <p className="mt-1 text-sm text-foreground">{user.lastName}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-muted-foreground/60">Email Address</label>
                      <p className="mt-1 text-sm text-foreground">{user.email}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-muted-foreground/60">User ID</label>
                      <p className="mt-1 text-sm text-foreground font-mono">{user.id}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Role & Permissions */}
              <div className="border-t border-border-subtle pt-6">
                <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
                  <Shield className="w-5 h-5 mr-2" />
                  Role & Permissions
                </h3>
                <div className="space-y-4">
                  <div className="bg-surface-glass rounded-lg p-4 border border-border-subtle">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-muted-foreground">Current Role:</span>
                      <GlassBadge variant={getRoleBadgeVariant(user.role)}>
                        {formatRoleLabel(user.role)}
                      </GlassBadge>
                    </div>
                    <p className="text-sm text-muted-foreground/60">
                      {getRoleDescription(user.role)}
                    </p>
                  </div>

                  <div className="bg-surface-glass rounded-lg p-4 border border-border-subtle">
                    <h4 className="text-sm font-medium text-foreground mb-3">Additional Privileges</h4>
                    <p className="text-xs text-muted-foreground/60 mb-3">
                      Grant specific create/update capabilities beyond the user&apos;s base role.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {privilegeOptions.map((option) => {
                        const checked = option.permissions.every(p => privilegeSet.has(p));
                        return (
                          <label
                            key={option.key}
                            className="flex items-center space-x-3 bg-surface-glass border border-border-subtle rounded-lg px-3 py-2 cursor-pointer hover:border-accent-blue transition-colors"
                          >
                            <input
                              type="checkbox"
                              className="rounded text-accent-blue focus:ring-accent-blue"
                              checked={checked}
                              disabled={!isEditing || isSubmitting}
                              onChange={() => togglePrivilege(option.permissions)}
                            />
                            <span className="text-sm text-foreground">{option.label}</span>
                          </label>
                        );
                      })}
                    </div>
                    {!isEditing && privilegeSet.size > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {Array.from(privilegeSet).map((perm) => (
                          <GlassBadge key={perm} variant="info">
                            {perm.replace(/_/g, ' ')}
                          </GlassBadge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Account Information */}
              <div className="border-t border-border-subtle pt-6">
                <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
                  <Calendar className="w-5 h-5 mr-2" />
                  Account Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground/60">Account Created</label>
                    <p className="mt-1 text-sm text-foreground">
                      {new Date(user.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground/60">Last Updated</label>
                    <p className="mt-1 text-sm text-foreground">
                      {new Date(user.updatedAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </GlassCard>
        </SectionReveal>
      </div>
    </div>
  );
};
